import "./css/SigningFlow.css";
import { STEPS } from "../constants/steps";
import { useEffect, useState } from "react";

export default function EligibilityQuestionsScreen({
    flowData,
    updateFlowData,
    flowContext,
    updateFlowContext,
    next,
    back,
    loading,
    setLoading,
    error,
    setError,
}) {

    const [answers, setAnswers] = useState({});
    const [otherMap, setOtherMap] = useState({});

    const profile = flowData?.profile || {};
    const profileName =
        [profile.firstName, profile.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() || "Customer";
    const profileInitials =
        [
            profile.firstName?.charAt(0),
            profile.lastName?.charAt(0)
        ]
            .filter(Boolean)
            .join("")
            .toUpperCase() || "?";
    

    const eligibility =
        flowData?.eligibility || {};

    const questions =
        eligibility.questions || [];

    const serviceIntro =
        eligibility.serviceIntro || "";

    /*Restore answers if the Guardian reopens the link. This is important for the resumable Guardian flow.*/
    useEffect(() => {
        if (flowData?.eligibilityAnswers) {
            setAnswers(flowData.eligibilityAnswers);
        }

        if (flowData?.eligibilityOther) {
            setOtherMap(flowData.eligibilityOther);
        }
    }, [
        flowData?.eligibilityAnswers,
        flowData?.eligibilityOther
    ]);

    /* const handleAnswer = (questionIndex, option, checked, fieldType) => {
        const key = `q_${questionIndex}`;
        setAnswers(prev => {
            if (fieldType === "checkbox") {
                const previous =
                    prev[key] || [];
                const updated =
                    checked
                        ? [
                            ...previous,
                            {
                                label: option.label
                            }
                        ]
                        : previous.filter(
                            item =>
                                item.label !== option.label
                        );
                return {
                    ...prev,
                    [key]: updated
                };
            }
            return {
                ...prev,
                [key]: checked
                    ? {
                        label: option.label
                    }
                    : null
            };
        });
    };*/
    
    // Exclusive answers are mutually exclusive with all other answers.
    const EXCLUSIVE_ANSWERS = [
        "none of the above",
        "none of these",
    ];
    const isExclusiveAnswer = (label) => {
        const normalized = label
            ?.trim()
            .replace(/\.$/, "")
            .toLowerCase();
        return EXCLUSIVE_ANSWERS.includes(normalized);
    };
    const handleAnswer = (questionIndex, option, checked, fieldType) => {
        const key = `q_${questionIndex}`;

        setAnswers(prev => {
            if (fieldType === "checkbox") {
                const previous = prev[key] || [];
                

                if (checked) {
                    // If  Exclusive answer is selected:
                    // remove all other answers.
                    if (isExclusiveAnswer(option.label)) {
                        return {
                            ...prev,
                            [key]: [
                                {
                                    label: option.label
                                }
                            ]
                        };
                    }


                    // Normal answer selected:
                    // remove any exclusive answer first.
                    const updated = [
                        ...previous.filter(
                            item => !isExclusiveAnswer(item.label)
                        ),
                        {
                            label: option.label
                        }
                    ];

                    return {
                        ...prev,
                        [key]: updated
                    };
                }

                return {
                    ...prev,
                    [key]: previous.filter(
                        item => item.label !== option.label
                    )
                };
            }

            return {
                ...prev,
                [key]: checked
                    ? {
                        label: option.label
                    }
                    : null
            };
        });
    };


    const handleOtherChange = (questionId, value) => {
        setOtherMap(prev => ({
            ...prev,
            [questionId]: value
        }));
    };
   

    const isOptionSelected = (
        question,
        questionIndex,
        option
    ) => {
        const key = `q_${questionIndex}`;
        if (question.fieldType === "checkbox") {
            return (answers[key] || [])
                .some(
                    item =>
                        item.label === option.label
                );
        }
        return (
            answers[key]?.label === option.label
        );
    };

    //only completeness validation. This should not determin warn/block
    const isOtherTextMissing = (
        question,
        questionIndex
    ) => {
        const key = `q_${questionIndex}`;
        const options =
            question.options || [];
        const otherOption =
            options.find(
                option =>
                    option.label
                        ?.trim()
                        .toLowerCase() === "other"
            );
        if (!otherOption) {
            return false;
        }
        if (
            !isOptionSelected(
                question,
                questionIndex,
                otherOption
            )
        ) {
            return false;
        }
        return !otherMap?.[question._id]?.trim();
    };
    const otherTextError = (question, questionIndex) => {
        if (
            isOtherTextMissing(question, questionIndex)
        ) {
            return "Please specify the other medication or condition.";
        }
        return "";
    };
    const allAnswered = questions.every(
        (question, index) => {
            const key = `q_${index}`;
            const answered =
                question.fieldType === "checkbox"
                    ? Array.isArray(answers[key]) &&
                    answers[key].length > 0
                    : !!answers[key];
            if (!answered) {
                return false;
            }
            if (
                isOtherTextMissing(
                    question,
                    index
                )
            ) {
                return false;
            }
            return true;
        }
    );
    
    const continueToNext = () => {
        if (loading) return;

        setError("");
        if (!allAnswered) {
            const missingOtherQuestion =
                questions.find(
                    (question, index) =>
                        isOtherTextMissing(
                            question,
                            index
                        )
                );
            if (missingOtherQuestion) {
                setError("Please specify the other medication or condition.");
            } else {
                setError("Please answer all eligibility questions.");
            }

            return;
        }
        next({
            action: "continue",
            eligibilityAnswers: answers,
            eligibilityOther: otherMap
        });
    };

    /*const goBack = () => {
        if (loading) return;
        setError("");
        next({
            action: "back"
        });
    };*/

    return (
       <div className={`page ${loading ? "page-loading" : ""}`}>
            
            {loading && (
                <div className="loading-overlay">
                    Loading...
                </div>
            )}

            {error && (
                <div className="text-red-600 mt-2">
                    {error}
                </div>
            )}            

            <div className="page-content">

                <div className="eligibility-client-card">

                    <div className="profile-avatar">
                        {profileInitials}
                    </div>

                    <div className="profile-details">

                        <div className="profile-name">
                            {profileName}
                        </div>

                        <div className="profile-status">
                            {flowData?.eligibility?.signerType === "minor"
                                ? "Minor"
                                : "Adult"}
                        </div>

                    </div>

                    <div className="eligibility-check">
                        ✓
                    </div>

                </div>

                <h3 className="eligibility-title">
                    Eligibility check
                </h3>

                {serviceIntro && (
                    <div
                        className="eligibility-text"
                        dangerouslySetInnerHTML={{
                            __html: serviceIntro
                        }}
                    />
                )}

                {questions.map((question, questionIndex) => (

                    <div
                        key={question._id || questionIndex}
                        className="eligibility-question"
                    >

                        <div className="eligibility-question-text">
                            {question.question}
                        </div>

                        <div className="eligibility-options">

                            {(question.options || []).map(
                                (option, optionIndex) => {

                                    const selected =
                                        isOptionSelected(
                                            question,
                                            questionIndex,
                                            option
                                        );

                                    const isOther =
                                        option.label
                                            ?.trim()
                                            .toLowerCase() ===
                                        "other";

                                    return (
                                        <div
                                            key={optionIndex}
                                            className="eligibility-option"
                                        >

                                            <label>

                                                <input
                                                    type={
                                                        question.fieldType ===
                                                        "checkbox"
                                                            ? "checkbox"
                                                            : "radio"
                                                    }
                                                    name={`q_${questionIndex}`}
                                                    checked={selected}
                                                    onChange={(e) =>
                                                        handleAnswer(
                                                            questionIndex,
                                                            option,
                                                            e.target.checked,
                                                            question.fieldType
                                                        )
                                                    }
                                                />

                                                <span>
                                                    {option.label}
                                                </span>

                                            </label>

                                            {isOther && selected && (
                                                <>
                                                    <input
                                                        className="eligibility-other-input"
                                                        type="text"
                                                        value={
                                                            otherMap?.[question._id] || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleOtherChange(
                                                                question._id,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Please specify"
                                                    />

                                                    {otherTextError(
                                                        question,
                                                        questionIndex
                                                    ) && (
                                                        <div className="eligibility-field-error">
                                                            {otherTextError(
                                                                question,
                                                                questionIndex
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                        </div>
                                    );
                                }
                            )}

                        </div>

                    </div>
                ))}

            </div>

            <div className="page-footer">

                {/*<button
                    className="back-button"
                    onClick={goBack}
                >
                    Back
                </button>*/}

                <button
                    className="primary-button"
                    onClick={continueToNext}
                    disabled={loading || !allAnswered}
                >
                    Continue
                    <span className="button-arrow">›</span>
                </button>

                <div className="powered-by">
                    Powered by <strong>DidYouSign</strong>
                </div>

            </div>

        </div>
    );
}